import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import styles from './SoftwarePage.module.css';
import { useLang } from '../../context/LanguageContext';

/* ── Icons ────────────────────────────────────────────── */
const IconApp = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
  </svg>
);
const IconDashboard = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/>
    <rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/>
  </svg>
);
const IconPortal = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/><path d="M16 11l2 2 4-4"/>
  </svg>
);
const IconERP = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9h18M3 15h18M9 3v18M15 3v18"/><rect x="3" y="3" width="18" height="18" rx="2"/>
  </svg>
);
const IconAPI = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/><line x1="12" y1="2" x2="12" y2="22"/>
  </svg>
);
const IconSaaS = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
  </svg>
);
const IconAutomation = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
  </svg>
);
const IconIntegration = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="12" r="4"/><circle cx="17" cy="12" r="4"/><path d="M11 12h2"/>
  </svg>
);

const deliverables = [
  { icon: <IconApp />,        label: { fr: 'Application web sur mesure',         en: 'Custom web application'         }, desc: { fr: 'Interface utilisateur complète adaptée à votre flux de travail.',       en: 'Complete user interface adapted to your workflow.'              } },
  { icon: <IconDashboard />,  label: { fr: 'Dashboard et tableaux de bord',      en: 'Dashboard & data visualization' }, desc: { fr: 'Visualisation de vos données métier en temps réel.',               en: 'Real-time visualization of your business data.'                 } },
  { icon: <IconPortal />,     label: { fr: 'Portail client ou extranet',          en: 'Client portal or extranet'      }, desc: { fr: 'Espace sécurisé pour vos clients, partenaires ou équipes.',       en: 'Secure space for your clients, partners or teams.'              } },
  { icon: <IconERP />,        label: { fr: 'Système de gestion interne',         en: 'Internal management system'     }, desc: { fr: 'ERP léger, CRM maison, outil métier sur mesure.',                  en: 'Lightweight ERP, in-house CRM, custom business tool.'           } },
  { icon: <IconAPI />,        label: { fr: 'API backend & microservices',        en: 'Backend API & microservices'    }, desc: { fr: 'Architecture découplée, scalable et documentée.',                 en: 'Decoupled, scalable and documented architecture.'               } },
  { icon: <IconSaaS />,       label: { fr: 'Plateforme SaaS',                    en: 'SaaS platform'                  }, desc: { fr: 'Produit logiciel multi-tenant prêt à commercialiser.',            en: 'Multi-tenant software product ready to commercialize.'          } },
  { icon: <IconAutomation />, label: { fr: 'Automatisation de processus',        en: 'Process automation'             }, desc: { fr: 'Remplacer les tâches répétitives par des workflows automatiques.', en: 'Replace repetitive tasks with automated workflows.'             } },
  { icon: <IconIntegration />,label: { fr: 'Intégration avec systèmes existants', en: 'Integration with existing systems'}, desc: { fr: 'Connecter votre nouvel outil à vos ERP, CRM et APIs tiers.',      en: 'Connect your new tool to your existing ERP, CRM and third-party APIs.' } },
];

const situations = [
  {
    num: '01',
    title: { fr: 'Les outils génériques ne suffisent plus',    en: 'Generic tools no longer cut it'        },
    desc:  { fr: "Excel, Notion, outils SaaS génériques — ils fonctionnent jusqu'à un certain point. Quand ils font perdre du temps au lieu d'en gagner, il est temps d'aller sur mesure.", en: "Excel, Notion, generic SaaS — they work up to a point. When they waste more time than they save, it's time to go custom." },
  },
  {
    num: '02',
    title: { fr: 'Vous avez un processus métier unique',        en: 'You have a unique business process'     },
    desc:  { fr: 'Votre activité a des spécificités que aucun outil standard ne couvre bien. Un outil dédié, pensé pour votre réalité, change tout.', en: 'Your business has specifics no standard tool covers well. A dedicated tool, designed for your reality, changes everything.' },
  },
  {
    num: '03',
    title: { fr: 'Vous avez une idée de produit logiciel',    en: 'You have a software product idea'       },
    desc:  { fr: 'Vous voulez valider rapidement un concept SaaS ou un outil interne. Je construis un MVP solide, pas un prototype fragile.', en: 'You want to quickly validate a SaaS concept or internal tool. I build a solid MVP, not a fragile prototype.' },
  },
];

const differentiators = [
  {
    title: { fr: 'Code propre et maintenable',           en: 'Clean and maintainable code'        },
    desc:  { fr: "Pas une boîte noire. Le code est documenté, structuré et lisible — vous pourrez le faire évoluer avec n'importe quel développeur.", en: "Not a black box. The code is documented, structured and readable — any developer can evolve it." },
  },
  {
    title: { fr: 'Architecture pensée pour évoluer',     en: 'Architecture built to scale'        },
    desc:  { fr: 'Les choix techniques sont faits pour que votre logiciel accompagne la croissance de votre entreprise sans tout reconstruire.', en: 'Technical choices are made so your software grows with your business without rebuilding from scratch.' },
  },
  {
    title: { fr: 'Communication directe',                en: 'Direct communication'               },
    desc:  { fr: "Pas de chef de projet intermédiaire. Vous parlez directement avec le développeur qui écrit le code — les décisions sont rapides et claires.", en: "No middleman PM. You talk directly to the developer writing the code — decisions are fast and clear." },
  },
];

const techs = ['Applications web', 'Applications bureau', 'APIs REST', 'Bases de données', 'Hébergement cloud', 'CI/CD', 'Automatisation', 'Intégrations tierces'];

/* ── Animation ─────────────────────────────────────────── */
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } } };
const fadeUp  = { hidden: { opacity: 0, y: 28, filter: 'blur(4px)' }, visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };

export default function SoftwarePage() {
  const delivRef   = useRef(null);
  const situRef    = useRef(null);
  const diffRef    = useRef(null);
  const delivInView  = useInView(delivRef,  { once: true, margin: '-60px' });
  const situInView   = useInView(situRef,   { once: true, margin: '-60px' });
  const diffInView   = useInView(diffRef,   { once: true, margin: '-60px' });
  const { lang } = useLang();

  const ui = {
    fr: {
      breadcrumb: 'Développement logiciel sur mesure',
      heroTitle:  'Un logiciel qui correspond exactement à',
      heroAccent: 'vos processus',
      heroSuffix: ", pas l\u2019inverse.",
      heroSub:    "Je développe des solutions logicielles adaptées aux processus internes de l\u2019entreprise \u2014 au lieu d\u2019imposer un outil générique qui ne s\u2019adapte jamais vraiment.",
      ctaPrimary: 'Démarrer le projet',
      ctaGhost:   'Voir les livrables',
      delivEy:    'Ce que je livre',
      delivT1:    'Chaque type de logiciel,',
      delivT2:    'construit avec soin.',
      situEy:     'Quand faire du sur mesure ?',
      situT1:     'Trois situations',
      situT2:     'qui appellent une solution dédiée.',
      diffEy:     'Ce qui me différencie',
      diffT1:     'Du code livré,',
      diffT2:     'pas du code abandonné.',
      techLabel:  'Technologies utilisées',
    },
    en: {
      breadcrumb: 'Custom software development',
      heroTitle:  'Software that fits exactly',
      heroAccent: 'your processes',
      heroSuffix: ', not the other way around.',
      heroSub:    'I develop software solutions adapted to your internal business processes — instead of forcing a generic tool that never truly fits.',
      ctaPrimary: 'Start the project',
      ctaGhost:   'See deliverables',
      delivEy:    'What I deliver',
      delivT1:    'Every type of software,',
      delivT2:    'built with care.',
      situEy:     'When to go custom?',
      situT1:     'Three situations',
      situT2:     'that call for a dedicated solution.',
      diffEy:     'What sets me apart',
      diffT1:     'Code that\u2019s delivered,',
      diffT2:     'not abandoned.',
      techLabel:  'Technologies used',
    },
  }[lang];

  return (
    <div className={styles.page}>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <div className={styles.gridLines} />
          <div className={styles.glow} />
        </div>
        <div className={styles.heroInner}>
          <motion.div className={styles.breadcrumb}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <Link to="/services" className={styles.breadcrumbLink}>Services</Link>
            <span className={styles.sep}>/</span>
            <span>{ui.breadcrumb}</span>
          </motion.div>

          <motion.h1 className={styles.heroTitle}
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}>
            {ui.heroTitle}{' '}
            <span className={styles.accent}>{ui.heroAccent}</span>{ui.heroSuffix}
          </motion.h1>

          <motion.p className={styles.heroSub}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: 'easeOut', delay: 0.24 }}>
            {ui.heroSub}
          </motion.p>

          <motion.div className={styles.heroCtas}
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: 'easeOut', delay: 0.36 }}>
            <Link to="/contact" className={styles.ctaPrimary}>
              {ui.ctaPrimary}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
            <a href="#livrables" className={styles.ctaGhost}>{ui.ctaGhost}</a>
          </motion.div>
        </div>
      </section>

      {/* ── LIVRABLES ── */}
      <section id="livrables" className={styles.section} ref={delivRef}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.delivEy}</p>
            <h2 className={styles.sectionTitle}>{ui.delivT1}<br />{ui.delivT2}</h2>
          </div>
          <motion.div className={styles.delivGrid} variants={stagger} initial="hidden" animate={delivInView ? 'visible' : 'hidden'}>
            {deliverables.map((d) => (
              <motion.div key={d.label.fr} className={styles.delivCard} variants={fadeUp}>
                <div className={styles.delivIcon}>{d.icon}</div>
                <div>
                  <p className={styles.delivLabel}>{d.label[lang]}</p>
                  <p className={styles.delivDesc}>{d.desc[lang]}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── QUAND FAIRE DU SUR MESURE ── */}
      <section className={`${styles.section} ${styles.sectionAlt}`} ref={situRef}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.situEy}</p>
            <h2 className={styles.sectionTitle}>{ui.situT1}<br />{ui.situT2}</h2>
          </div>
          <motion.div className={styles.sitGrid} variants={stagger} initial="hidden" animate={situInView ? 'visible' : 'hidden'}>
            {situations.map((s) => (
              <motion.div key={s.num} className={styles.sitCard} variants={fadeUp}>
                <span className={styles.sitNum}>{s.num}</span>
                <h3 className={styles.sitTitle}>{s.title[lang]}</h3>
                <p className={styles.sitDesc}>{s.desc[lang]}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── DIFFÉRENCIATEURS ── */}
      <section className={styles.section} ref={diffRef}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.diffEy}</p>
            <h2 className={styles.sectionTitle}>{ui.diffT1}<br />{ui.diffT2}</h2>
          </div>
          <motion.div className={styles.diffList} variants={stagger} initial="hidden" animate={diffInView ? 'visible' : 'hidden'}>
            {differentiators.map((d) => (
              <motion.div key={d.title.fr} className={styles.diffItem} variants={fadeUp}>
                <span className={styles.diffArrow}>→</span>
                <div>
                  <p className={styles.diffTitle}>{d.title[lang]}</p>
                  <p className={styles.diffDesc}>{d.desc[lang]}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── TECHNOLOGIES ── */}
      <section className={`${styles.section} ${styles.sectionAlt}`} data-aos="fade-up">
        <div className={styles.container}>
          <div className={styles.techRow}>
            <p className={styles.techLabel}>{ui.techLabel}</p>
            <div className={styles.techBadges}>
              {techs.map((t) => (
                <span key={t} className={styles.techBadge}>{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>


    </div>
  );
}
